import { Injectable } from '@angular/core';
import { ApiService } from './api.service';

// Reads the global config declared in general.script.js
declare function GetConfig(): any;

/**
 * LoggingService
 * ─────────────────────────────────────────────────────────────────────────────
 * Singleton service that ships structured log entries from Angular components
 * to the backend endpoint  POST /message/log/client.
 *
 * Master switch: set  enableLogging: false  in general.script.js to make this
 * service a complete no-op at runtime — no HTTP calls, no overhead.
 *
 * Usage:
 *   this._log.log(this.UserId, 'Admin', 'GET_MESSAGES',
 *                 'Fetching messages for friendId=55', 'start', { friendId: 55 });
 * ─────────────────────────────────────────────────────────────────────────────
 */
@Injectable({ providedIn: 'root' })
export class LoggingService {
  private enabled: boolean;

  constructor(private _api: ApiService) {
    try {
      this.enabled = GetConfig().enableLogging === true;
    } catch (e) {
      // If GetConfig() fails for any reason, disable logging gracefully
      this.enabled = false;
    }
  }

  /**
   * Ship one log entry to the backend.
   *
   * @param userId      - ID of the acting user (number or string)
   * @param role        - 'Admin' | 'Customer' | 'System'
   * @param action      - Short action name, e.g. 'GET_MESSAGES', 'SEND_MESSAGE'
   * @param description - Human-readable summary of what happened
   * @param status      - 'start' | 'success' | 'error'  (default: 'info')
   * @param meta        - Any extra key/value context (default: {})
   */
  log(
    userId: any,
    role: string,
    action: string,
    description: string,
    status: 'start' | 'success' | 'error' | 'info' = 'info',
    meta: any = {}
  ): void {
    if (!this.enabled) return; // pure no-op — no I/O when disabled

    const entry = { userId, role, action, description, status, meta };

    this._api
      .postTypeRequest('message/log/client', entry)
      .subscribe({
        // Silently discard errors — logging must never break the app
        error: () => {}
      });
  }
}
